/**
 * Web-push for the firm panel. Subscribes the staff user's browser (mobile or
 * desktop) to notifications for team-chat DMs/broadcasts and incoming client
 * messages. Talks to /manager/push/* (auth = cognito firm user); those handlers
 * wrap responses as { success, data }.
 */
import apiClient from './client';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function getPublicKey(): Promise<string> {
  const r = await apiClient.get('/manager/push/public-key');
  return r.data?.data?.publicKey || '';
}

const pushSupported = () =>
  typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

async function subscribeAndRegister(): Promise<boolean> {
  const reg = await navigator.serviceWorker.ready;
  const publicKey = await getPublicKey();
  if (!publicKey) return false;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource });
  }
  await apiClient.post('/manager/push/subscribe', sub.toJSON());
  return true;
}

/** First-time enable: prompts for permission, then subscribes. */
export async function enablePanelPush(): Promise<{ ok: true } | { ok: false; reason: string }> {
  if (!pushSupported()) return { ok: false, reason: 'Notifications are not supported in this browser' };
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return { ok: false, reason: 'Permission denied' };
  try {
    const done = await subscribeAndRegister();
    return done ? { ok: true } : { ok: false, reason: 'Server push not configured' };
  } catch (e: any) {
    return { ok: false, reason: e?.message || 'Failed to enable notifications' };
  }
}

/** Silent: if permission was already granted, make sure we have a live subscription. */
export async function ensurePanelPushSubscribed(): Promise<void> {
  if (!pushSupported() || Notification.permission !== 'granted') return;
  try { await subscribeAndRegister(); } catch { /* ignore */ }
}

export async function disablePanelPush(): Promise<void> {
  if (!pushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await apiClient.post('/manager/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => {});
      await sub.unsubscribe();
    }
  } catch { /* ignore */ }
}

export function panelPushPermission(): 'unsupported' | 'granted' | 'denied' | 'default' {
  if (!pushSupported()) return 'unsupported';
  return Notification.permission as 'granted' | 'denied' | 'default';
}
